import React, {useEffect, useRef} from "react";
import type {Ring} from "./wm";

interface Props {
  slideW: number;
  slideH: number;
  wmPolygon: Ring[];
  // Viv viewState: {target: [x, y, z], zoom: number}
  viewState: any;
  // Live canvas dims so we can draw the viewport box at the right size.
  canvasW: number;
  canvasH: number;
}

const MINI_MAX = 180;  // px — bottom-right thumbnail size

/**
 * Overview minimap drawn into a small canvas.
 *  - Light gray rect = slide bounds.
 *  - White polylines  = WM mask polygons (Otsu/morphology from OLIG2).
 *  - Cyan rect        = current viewport (what's visible in the main view).
 *  - Cyan dot         = viewport center (useful at extreme zoom-out).
 *
 * The minimap is intentionally read-only; reviewers just glance at it to
 * confirm they're parked inside WM before committing a pick.
 */
export function Minimap({slideW, slideH, wmPolygon, viewState, canvasW, canvasH}: Props) {
  const ref = useRef<HTMLCanvasElement>(null);

  // Aspect-fit the minimap to a MINI_MAX bounding box.
  const aspect = slideW / Math.max(1, slideH);
  const miniW = aspect >= 1 ? MINI_MAX : Math.round(MINI_MAX * aspect);
  const miniH = aspect >= 1 ? Math.round(MINI_MAX / aspect) : MINI_MAX;
  const sx = miniW / slideW;
  const sy = miniH / slideH;

  useEffect(() => {
    const cv = ref.current;
    if (!cv) return;
    const dpr = window.devicePixelRatio || 1;
    cv.width  = Math.round(miniW * dpr);
    cv.height = Math.round(miniH * dpr);
    cv.style.width  = `${miniW}px`;
    cv.style.height = `${miniH}px`;
    const ctx = cv.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    // Background — slide area
    ctx.fillStyle = "#0b1220";
    ctx.fillRect(0, 0, miniW, miniH);
    ctx.strokeStyle = "#3a4255";
    ctx.lineWidth = 1;
    ctx.strokeRect(0.5, 0.5, miniW - 1, miniH - 1);

    // WM polygons — white outlines
    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = 1.25;
    ctx.lineJoin = "round";
    for (const ring of wmPolygon) {
      if (!ring || ring.length < 3) continue;
      ctx.beginPath();
      // Decimate rings to ≤200 pts for cheap redraws.
      const step = Math.max(1, Math.floor(ring.length / 200));
      const [x0, y0] = ring[0];
      ctx.moveTo(x0 * sx, y0 * sy);
      for (let i = step; i < ring.length; i += step) {
        const [x, y] = ring[i];
        ctx.lineTo(x * sx, y * sy);
      }
      ctx.closePath();
      ctx.stroke();
    }

    // Viewport rect — cyan
    if (viewState && Array.isArray(viewState.target) && typeof viewState.zoom === "number") {
      const z = Math.pow(2, viewState.zoom);   // screen px per image px
      const visW = canvasW / z;
      const visH = canvasH / z;
      const cx = viewState.target[0];
      const cy = viewState.target[1];
      const rx = (cx - visW / 2) * sx;
      const ry = (cy - visH / 2) * sy;
      const rw = visW * sx;
      const rh = visH * sy;
      ctx.strokeStyle = "#22d3ee";
      ctx.lineWidth = 2;
      ctx.strokeRect(rx, ry, rw, rh);
      // Center dot for when the viewport rect shrinks to a pixel
      ctx.fillStyle = "#22d3ee";
      ctx.beginPath();
      ctx.arc(cx * sx, cy * sy, 2.5, 0, Math.PI * 2);
      ctx.fill();
    }
  }, [slideW, slideH, wmPolygon, viewState, canvasW, canvasH, miniW, miniH, sx, sy]);

  return (
    <div style={{
      position: "absolute", right: 12, bottom: 12, zIndex: 10,
      background: "rgba(0, 0, 0, 0.55)",
      padding: 6, borderRadius: 6,
      border: "1px solid #3a4255",
      pointerEvents: "none",
      fontFamily: "system-ui, sans-serif",
      color: "#cbd2e0", fontSize: 10,
    }}>
      <div style={{display: "flex", gap: 8, marginBottom: 4, alignItems: "center"}}>
        <span style={{display: "inline-block", width: 10, height: 10,
                      border: "2px solid #ffffff", borderRadius: 1}}/>
        <span>WM mask</span>
        <span style={{display: "inline-block", width: 10, height: 10,
                      border: "2px solid #22d3ee", borderRadius: 1, marginLeft: 6}}/>
        <span>view</span>
      </div>
      <canvas ref={ref} />
    </div>
  );
}
